/**
 * LoginForm - Initial login form with mobile input and email option
 * Flow:
 * 1. Show mobile number input only
 * 2. Below input show "or Sign in with email" link
 * 3. When clicked, show email input with password/OTP options
 * 4. Check if email user exists, if not show error and go back to mobile
 * 5. Show terms and conditions text
 */
import React, { useState } from "react";
import { useAuthContext } from "../../context/AuthContext";
import { useAuth } from "../../hooks/useAuth";
import { useAuthForm } from "../../hooks/useAuthForm";
import { AUTH_MODES, INPUT_TYPES, VALIDATION_PATTERNS } from "../../types";
import { AuthHeader, AuthInput, AuthButton } from "../ui";
import { getConsentVersions } from '../../services/authApi';

function LoginForm() {
  const { loading, switchMode, setMode, mode } = useAuthContext();
  const { sendOtp, checkPasswordAndProceed } = useAuth();
  const {
    identifier,
    identifierType,
    error,
    setError,
    handleMobileChange,
    handleEmailChange,
    setIdentifier,
    setIdentifierType,
  } = useAuthForm();

  // UI state
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showPasswordOption, setShowPasswordOption] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  // Get consent versions for T&C links
  const [consentInfo, setConsentInfo] = useState(null);
  React.useEffect(() => {
    // getConsentVersions().then(res => {
    //   const versions = res?.data?.find?.(v => v.userType === 'PATIENT_B2C') || res?.data?.[0];
    //   if (versions) setConsentInfo(versions);
    // }).catch(() => {});
    getConsentVersions().then(res => {
  setConsentInfo(res);
}).catch(() => {});
  }, []);

  // Handle mobile number submit
  const handleMobileSubmit = async (e) => {
    e.preventDefault();

    if (!identifier || identifier.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!VALIDATION_PATTERNS.MOBILE.test(identifier)) {
      setError("Mobile number must start with 6, 7, 8, or 9");
      return;
    }

    // Send OTP for mobile
    const success = await sendOtp();
    if (success) {
      setMode(AUTH_MODES.OTP_VERIFY);
    }
  };

  // Handle "Sign in with email" click
  const handleEmailLoginClick = () => {
    setShowEmailLogin(true);
    setIdentifier("");
    setIdentifierType(INPUT_TYPES.EMAIL);
    setError("");
    setShowPasswordOption(false);
  };

  // Handle back to mobile
  const handleBackToMobile = () => {
    setShowEmailLogin(false);
    setIdentifier("");
    setIdentifierType(INPUT_TYPES.MOBILE);
    setError("");
    setShowPasswordOption(false);
  };

  // Handle email submit - check if user exists first
  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!identifier || !VALIDATION_PATTERNS.EMAIL.test(identifier)) {
      setError("Please enter a valid email address");
      return;
    }

    setEmailCheckLoading(true);
    setError("");

    try {
      const result = await checkPasswordAndProceed();

      if (result === "password") {
        setShowPasswordOption(true);
      } else if (result === "otp") {
        const success = await sendOtp();
        if (success) setMode(AUTH_MODES.OTP_VERIFY);
      } else if (result === "not_found") {
        // ✅ New email user → send OTP → signup flow
        const success = await sendOtp();
        if (success) setMode(AUTH_MODES.OTP_VERIFY);
      }
    } catch (err) {
      const success = await sendOtp();
      if (success) setMode(AUTH_MODES.OTP_VERIFY);
    } finally {
      setEmailCheckLoading(false);
    }
  };

  // Handle password login
  const handlePasswordLogin = () => {
    setMode(AUTH_MODES.PASSWORD_LOGIN);
  };

  // Handle send OTP for email
  const handleSendEmailOtp = async () => {
    const success = await sendOtp();
    if (success) {
      setMode(AUTH_MODES.OTP_VERIFY);
      setShowPasswordOption(false);
    }
  };

  // Determine if we're in signup mode
  const isSignup = mode === AUTH_MODES.SIGNUP;

  // Validation helpers
  const isMobileValid =
    identifier &&
    identifier.length === 10 &&
    VALIDATION_PATTERNS.MOBILE.test(identifier);
  const isEmailValid = identifier && VALIDATION_PATTERNS.EMAIL.test(identifier);
  const hasError = error && error.length > 0;
  console.log(consentInfo, "sdvihsdohvsdguvdsvds");
  return (
    <div className="relative p-6 md:p-8 bg-white">
      <AuthHeader
        title={isSignup ? "Sign Up" : "Login"}
        subtitle={isSignup ? "Create your account to get started" : ""}
      />

      {/* Mobile Number Input - Default View */}
      {!showEmailLogin && (
        <form onSubmit={handleMobileSubmit} className="space-y-5">
          <AuthInput
            label="Mobile Number"
            type="tel"
            value={identifier}
            onChange={handleMobileChange}
            placeholder="Enter 10-digit mobile number"
            error={error}
            disabled={loading}
            // autoFocus
            maxLength={10}
            inputMode="numeric"
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            }
          />
          {/* "Or Sign in with email" Divider - Only for mobile view */}
          {!showEmailLogin && (
            <>
              <div className="my-2">
                <div className="relative flex items-center">
                  <div className="flex-grow border-t border-[#e8e8e8]" />
                  <span className="px-4 text-sm text-gray-500">or</span>
                  <div className="flex-grow border-t border-[#e8e8e8]" />
                </div>
                <div className="mt-2 text-center">
                  <button
                    type="button"
                    onClick={handleEmailLoginClick}
                    className="text-sm text-[#004171] hover:underline font-medium"
                  >
                    Sign in with email
                  </button>
                </div>
              </div>
            </>
          )}
          <AuthButton
            type="submit"
            loading={loading}
            disabled={!isMobileValid || hasError}
          >
            Send OTP
          </AuthButton>
        </form>
      )}

      {/* Email Login View */}
      {showEmailLogin && !showPasswordOption && (
        <form onSubmit={handleEmailSubmit} className="space-y-5">
          <div className="flex items-center mb-4">
            <button
              type="button"
              onClick={handleBackToMobile}
              className="text-gray-500 hover:text-[#004171] transition-colors mr-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-sm text-gray-600">Back to mobile login</span>
          </div>

          <AuthInput
            label="Email Address"
            type="email"
            value={identifier}
            onChange={handleEmailChange}
            placeholder="Enter your email address"
            error={error}
            disabled={loading || emailCheckLoading}
            autoFocus
            maxLength={50}
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            }
          />

          <AuthButton
            type="submit"
            loading={loading || emailCheckLoading}
            disabled={!isEmailValid || hasError}
          >
            Continue
          </AuthButton>
        </form>
      )}

      {/* Password/OTP Options for Email */}
      {showEmailLogin && showPasswordOption && (
        <div className="space-y-4">
          <div className="flex items-center mb-4">
            <button
              type="button"
              onClick={() => setShowPasswordOption(false)}
              className="text-gray-500 hover:text-[#004171] transition-colors mr-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-sm text-gray-600">Change email</span>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-600">Signing in as:</p>
            <p className="font-medium text-[#004171]">{identifier}</p>
          </div>

          <AuthButton
            type="button"
            onClick={handlePasswordLogin}
            loading={loading}
          >
            Continue with Password
          </AuthButton>

          <div className="text-center">
            <button
              type="button"
              onClick={handleSendEmailOtp}
              disabled={loading}
              className="text-sm text-[#004171] hover:underline disabled:opacity-50"
            >
              Or login with OTP
            </button>
          </div>
        </div>
      )}

      {/* Forgot Password - Only for Login mode */}
      {!isSignup && !showEmailLogin && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => switchMode(AUTH_MODES.FORGOT_PASSWORD)}
            className="text-sm text-gray-500 hover:text-[#004171] transition-colors"
          >
            Forgot Password?
          </button>
        </div>
      )}

      {/* Terms and Conditions */}
      <div className="mt-6 text-sm text-gray-500 text-center">
  <p className="mb-1">By continuing, you agree to our</p>

  <p className="m-0 leading-tight">
    
    <a
      href={
        consentInfo?.tcPdf
          ? `${process.env.REACT_APP_IMG_BASEURL}${consentInfo.tcPdf}`
          : "#"
      }
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#004171] underline hover:text-[#004171] cursor-pointer"
      onClick={(e) => {
        console.log(
          "Terms clicked:",
          consentInfo?.tcPdf
            ? `${process.env.REACT_APP_IMG_BASEURL}${consentInfo.tcPdf}`
            : "no URL"
        );
        if (!consentInfo?.tcPdf) e.preventDefault();
      }}
    >
      Terms of Use
    </a>

    {" & "}

    <a
      href={
        consentInfo?.privacyPdf
          ? `${process.env.REACT_APP_IMG_BASEURL}${consentInfo.privacyPdf}`
          : "#"
      }
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#004171] underline hover:text-[#004171] cursor-pointer"
      onClick={(e) => {
        console.log(
          "Privacy clicked:",
          consentInfo?.privacyPdf
            ? `${process.env.REACT_APP_IMG_BASEURL}${consentInfo.privacyPdf}`
            : "no URL"
        );
        if (!consentInfo?.privacyPdf) e.preventDefault();
      }}
    >
      Privacy Policy
    </a>

  </p>
</div>
    </div>
  );
}

export default LoginForm;
